import { apiSlice } from './apiSlice';

/**
 * Admin console API for tournament seasons (/api/seasons) and the scenario
 * debug playground (/api/scenario-debug) — the backend endpoints are
 * admin-gated ('users.update'), matching the /admin menu gating.
 */

export interface SeasonSummary {
  id: string;
  scenarioTemplateId: string;
  version: number;
  mode: string;
  status: string;
  opensAt: string;
  closesAt: string;
  attemptsLimit: number;
  engineVersion: string;
  scoringVersion: string;
  verificationPolicy: 'inline' | 'lazy';
  closedAt?: string | null;
  manifest: Record<string, unknown>;
  signature: string;
  template?: ScenarioTemplate;
}

export interface SeasonCloseResult {
  seasonId: string;
  closed: boolean;
  alreadyClosed: boolean;
  closedAt: string | null;
  abandoned?: number;
  verified?: number;
  rejected?: number;
  placementsIssued: Array<{ rank?: number; attemptId?: string; billingAccountId: string; amount: number; posted?: boolean }>;
}

export interface SeasonScore {
  scoringVersion?: string;
  provisional?: boolean;
  scoreTier: number | null;
  passedCount: number | null;
  total: number | null;
  completed?: boolean;
  primaryMetricKey?: string | null;
  primaryMetricValue?: number | null;
  metrics?: Record<string, number> | null;
  rankKey?: string;
  goals?: Array<{ id: string | null; passed: boolean }>;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string | null;
  scoreTier: number | null;
  passedCount: number | null;
  total: number | null;
  primaryMetricKey: string | null;
  primaryMetricValue: number | null;
  metrics: Record<string, number> | null;
  rankKey: string;
  submittedAt: string;
  verified: boolean;
  provisional: boolean;
  replayDigest: string | null;
}

export interface LeaderboardResponse {
  seasonId: string;
  scoringVersion: string;
  engineVersion: string;
  you: string | null;
  entries: LeaderboardEntry[];
}

export interface SeasonAttempt {
  id: string;
  seasonId: string;
  userId: string;
  displayName: string | null;
  status: 'submitted' | 'verified' | 'rejected';
  scoreSource: 'server' | 'client';
  rankKey: string;
  serverScore: SeasonScore | null;
  provisionalScore: SeasonScore | null;
  clientScore: Record<string, unknown> | null;
  replayDigest: string | null;
  eventCount: number | null;
  submittedAt: string;
  verifiedAt: string | null;
  engineVersion: string;
}

export interface SeasonAttemptsResponse {
  seasonId: string;
  verificationPolicy: 'inline' | 'lazy';
  attempts: SeasonAttempt[];
}

export interface VerifyResult {
  seasonId: string;
  checked: number;
  verified: number;
  rejected: number;
  pendingLeft: number;
  results: Array<{
    attemptId: string;
    userId: string;
    status: string;
    claimedRankKey: string;
    officialRankKey: string;
    replayDigest: string;
  }>;
}

export interface CreateSeasonBody {
  templateId: string;
  version?: number | null;
  mode?: string;
  opensAt: string;
  closesAt: string;
  attemptsLimit?: number;
  verificationPolicy?: 'inline' | 'lazy';
}

// ── Scenario debug playground ──────────────────────────────────────────────

export interface ScenarioGoal {
  id: string;
  label?: string;
  metric: string;
  operator?: string;
  target: unknown;
  actionCount?: { action: string; min?: number; max?: number; exact?: number };
}

export interface ScenarioTemplateBed {
  id: string;
  plotId: string;
  name?: string;
  cells: Array<{ row: number; col: number }>;
  /** Семена почти всегда его не задают — движок падает на дефолт 'loam'. */
  soilType?: string;
  initialState?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ScenarioTemplatePlanting {
  id: string;
  bedId: string;
  crop: string;
  quantity?: number;
  healthScore?: number;
  moisturePct?: number;
  growthProfile?: Record<string, unknown>;
  initialState?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ScenarioTemplate {
  scenarioTemplateId: string;
  version: number;
  slug: string;
  title: string;
  kind: string;
  durationDays: number;
  allowRetroEdit: boolean;
  terminalDate?: string;
  worldSpec: {
    startDate: string;
    timezone?: string;
    expectedEvents?: number;
    sessionConfig?: Record<string, unknown>;
    weatherSeries: Array<{
      date: string;
      tempMin: number;
      tempMax: number;
      precipitationMm?: number;
      humidityPct?: number;
      windSpeed?: number;
    }>;
    plots?: Array<{ id: string; name?: string; cells: Array<{ row: number; col: number; type?: string }> }>;
    beds: ScenarioTemplateBed[];
    plantings: ScenarioTemplatePlanting[];
    structuralObjects?: unknown[];
  };
  validationRules: Array<{ action: string; scope: { entityType: string; entityIds: string[] } }>;
  goals: ScenarioGoal[];
  scoring: { tiers: Array<{ tier: number; minPassed: number }> };
  comparison?: Record<string, unknown> | null;
  narrative?: { intro?: string; objective?: string; hint?: string };
  visibility?: string;
  /** Лесенка призов: { firstClear: { '1'|'2'|'3': Reward[] }, upgrade } */
  rewards?: Record<string, unknown> | null;
}

export interface DebugEvent {
  id: string;
  type: string;
  date: string;
  target: Record<string, string>;
  payload: Record<string, unknown>;
}

export interface DebugDayPlanting {
  lifecyclePhase?: string;
  vegetationStage?: string;
  height?: number;
  crownDiameter?: number;
  healthScore?: number;
  soilMoisturePct?: number;
  accumulatedGDD?: number;
  dailyGDD?: number;
  dliMol?: number;
  harvestAvailableKg?: number;
  fruitRipeKg?: number;
  fruitUnripeKg?: number;
  fruitGreenDays?: number;
  cumulativeHarvestCollectedKg?: number;
  phaseWeightedStress?: number;
  stressFactors?: Record<string, number>;
  isDead?: boolean;
  [key: string]: unknown;
}

export interface DebugDayBed {
  moisturePct?: number;
  nitrogen?: number;
  phosphorus?: number;
  potassium?: number;
  nutrientPools?: unknown;
  [key: string]: unknown;
}

export interface DebugDay {
  dayIndex: number;
  simDate: string;
  plantings: Record<string, DebugDayPlanting>;
  beds: Record<string, DebugDayBed>;
  weather?: { tempMin?: number; tempMax?: number; precipitationMm?: number; humidityPct?: number };
}

/**
 * `scenario_state.engine.diagnostics` — стабильный канал предупреждений реплея
 * (docs/SCENARIO_DIAGNOSTICS.md). Логика строится на `code`, не на `message`.
 * Практически значимый код один: `scenario_event_target_not_found` — событие
 * указало на несуществующий id, реплей его молча проигнорировал, и прогон
 * выглядит как «действие ничего не сделало».
 */
export interface ScenarioDiagnostic {
  tick?: number | null;
  date?: string | null;
  severity: 'info' | 'warning' | 'error';
  code: string;
  message?: string;
  target?: { kind?: string; id?: string } | null;
  event_id?: string | null;
}

/**
 * Экономика ПРОХОЖДЕНИЯ (RFC_SCENARIO_AUTHORING_ARCHITECTURE §4). Деньги не
 * покидают попытку: это не кошелёк аккаунта, а бюджет одной партии. Суммы —
 * целые минимальные единицы, склад — килограммы с точностью до грамма.
 */
export interface ScenarioEconomyItem {
  id: string;
  kind: 'covering' | 'supply';
  title: string;
  price: number;
  stock: number | null;
  maxWindowDays: number | null;
  reusable: boolean;
  owned: number;
  bought: number;
}

export interface ScenarioEconomyBattery {
  capacity: number;
  charge: number;
  costPerDay: number;
  regenPerDay: number;
}

export interface ScenarioEconomyApplied {
  id: string | null;
  type: string;
  date: string;
  balanceAfter: number;
  crop?: string;
  quantityKg?: number;
  amount?: number;
  unitPrice?: number;
  itemId?: string;
  quantity?: number;
  bedId?: string;
  activeFrom?: string;
  activeTo?: string | null;
}

/**
 * Отклонённая команда. Для автора это главный канал обратной связи: движок
 * НИЧЕГО не сделал, и здесь написано почему — «не хватило денег», «нечего
 * продавать», «укрытие в прошлое». Без него отказ выглядел бы как пропавшее
 * действие.
 */
export interface ScenarioEconomyRejected {
  id: string | null;
  type: string | null;
  date: string | null;
  code: string;
  message: string;
  [key: string]: unknown;
}

export interface ScenarioEconomyState {
  currency: string;
  minorUnitsPerUnit: number;
  balance: number;
  startingBalance: number;
  revenue: number;
  spent: number;
  storageKg: Record<string, number>;
  soldKg: Record<string, number>;
  harvestedKg: Record<string, number>;
  inventory: Record<string, number>;
  purchased: Record<string, number>;
  battery: ScenarioEconomyBattery | null;
  catalog: ScenarioEconomyItem[];
  sellPrices: Record<string, number>;
  applied: ScenarioEconomyApplied[];
  rejected: ScenarioEconomyRejected[];
  unscheduled: Array<{ id: string | null; code: string }>;
  /** Ресурсная батарейка кончилась — календарь встал раньше дедлайна. */
  batteryExhausted?: boolean;
  stoppedAtDate?: string;
}

/** Команда экономики попытки. Живёт отдельно от лога ухода — см. ScenarioEconomy. */
export interface ScenarioEconomyCommand {
  id: string;
  type: 'sell_harvest' | 'buy_item' | 'install_covering';
  date: string;
  payload: Record<string, unknown>;
  /** Позиция в логе: решает порядок команд ОДНОГО дня. */
  sequence?: number;
}

export interface DebugRunResponse {
  slug: string;
  cursorDate: string;
  terminal: boolean;
  daysAdvanced: number;
  metrics: Record<string, unknown>;
  goalsProgress: {
    completed: boolean;
    passedCount: number;
    total: number;
    scoreTier: number | null;
    /** Обязательные цели (§5): без них тир не выдаётся вовсе. */
    requiredPassed?: number;
    requiredTotal?: number;
    requiredSatisfied?: boolean;
    bonusPassed?: number;
    bonusTotal?: number;
    goals: Array<{
      id: string;
      label: string;
      metric: string;
      value: unknown;
      operator: string;
      target: unknown;
      passed: boolean;
      required?: boolean;
    }>;
  };
  economy: ScenarioEconomyState | null;
  economySpecErrors?: Array<{ code: string; path: string; message: string }>;
  days: DebugDay[];
  diagnostics: ScenarioDiagnostic[];
}

export const seasonsAdminApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    listSeasons: build.query<{ seasons: SeasonSummary[] }, void>({
      query: () => ({ url: '/seasons' }),
      providesTags: [{ type: 'Season' as const, id: 'LIST' }],
    }),
    getSeason: build.query<{ season: SeasonSummary }, string>({
      query: (id) => ({ url: `/seasons/${id}` }),
      providesTags: (_r, _e, id) => [{ type: 'Season' as const, id }],
    }),
    createSeason: build.mutation<SeasonSummary, CreateSeasonBody>({
      query: (body) => ({ url: '/seasons', method: 'POST', body }),
      invalidatesTags: [{ type: 'Season' as const, id: 'LIST' }],
    }),
    getSeasonLeaderboard: build.query<LeaderboardResponse, string>({
      query: (id) => ({ url: `/seasons/${id}/leaderboard` }),
      providesTags: (_r, _e, id) => [{ type: 'SeasonAttempts' as const, id }],
    }),
    getSeasonAttempts: build.query<SeasonAttemptsResponse, string>({
      query: (id) => ({ url: `/seasons/${id}/attempts` }),
      providesTags: (_r, _e, id) => [{ type: 'SeasonAttempts' as const, id }],
    }),
    verifySeason: build.mutation<VerifyResult, { seasonId: string; attemptIds?: string[]; limit?: number }>({
      query: ({ seasonId, ...body }) => ({ url: `/seasons/${seasonId}/verify`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { seasonId }) => [{ type: 'SeasonAttempts' as const, id: seasonId }],
    }),
    closeSeason: build.mutation<SeasonCloseResult, { seasonId: string; force?: boolean }>({
      query: ({ seasonId, force }) => ({ url: `/seasons/${seasonId}/close`, method: 'POST', body: { force: force === true } }),
      invalidatesTags: (_r, _e, { seasonId }) => [
        { type: 'SeasonAttempts' as const, id: seasonId },
        { type: 'Season' as const, id: seasonId },
        { type: 'Season' as const, id: 'LIST' },
      ],
    }),
    getDebugTemplates: build.query<{ templates: ScenarioTemplate[] }, void>({
      query: () => ({ url: '/scenario-debug/templates' }),
    }),
    runDebugScenario: build.mutation<DebugRunResponse, {
      slug: string;
      events?: DebugEvent[];
      /** Лог экономики попытки — отдельный от ухода (см. ScenarioEconomyCommand). */
      commands?: ScenarioEconomyCommand[];
      /** Наложить блок экономики на сид ТОЛЬКО для этого прогона (ничего не пишется). */
      economy?: Record<string, unknown> | null;
      cursorDate?: string | null;
    }>({
      query: (body) => ({ url: '/scenario-debug/run', method: 'POST', body }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useListSeasonsQuery,
  useGetSeasonQuery,
  useCreateSeasonMutation,
  useGetSeasonLeaderboardQuery,
  useGetSeasonAttemptsQuery,
  useVerifySeasonMutation,
  useCloseSeasonMutation,
  useGetDebugTemplatesQuery,
  useRunDebugScenarioMutation,
} = seasonsAdminApi;
